import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  MdNorthEast,
  MdCheckCircle,
  MdErrorOutline,
  MdShield,
  MdInfoOutline,
} from "react-icons/md";

// Type-detection from notification text. Looks at title + message in both
// English and Arabic so notifications coming from the backend in either
// language get the right colour/icon without needing a server-side `type`.
const detectType = (notification = {}) => {
  if (notification.type) return notification.type;
  const s = `${notification.title || ""} ${notification.message || ""}`.toLowerCase();

  if (/credit|balance|added|topup|top-up|أضيف|إضافة|رصيد|تعبئة/.test(s)) return "credit";
  if (/fail|error|declin|refus|فشل|خطأ|رفض/.test(s))                    return "fail";
  if (/login|password|security|2fa|otp|تسجيل|كلمة المرور|أمان/.test(s)) return "security";
  if (/success|complete|successful|approved|نجح|نجاح|تم|اكتمل/.test(s)) return "success";
  return "info";
};

// Per-type colour palette tuned for a banking/fintech feel: subtle tints with
// strong accent bars, no gradients on the body.
const TYPE_THEME = {
  credit:   { accent: "#10B981", tint: "rgba(16, 185, 129, 0.10)", Icon: MdNorthEast,    label: "Credit" },
  success:  { accent: "#3B82F6", tint: "rgba(59, 130, 246, 0.10)", Icon: MdCheckCircle,  label: "Transaction" },
  fail:     { accent: "#EF4444", tint: "rgba(239, 68, 68, 0.10)",  Icon: MdErrorOutline, label: "Failed" },
  security: { accent: "#F59E0B", tint: "rgba(245, 158, 11, 0.10)", Icon: MdShield,       label: "Security" },
  info:     { accent: "#64748B", tint: "rgba(100, 116, 139, 0.10)", Icon: MdInfoOutline,  label: "Notice" },
};

export const NOTIFICATION_TYPE_THEME = TYPE_THEME;
export const detectNotificationType = detectType;

const AUTO_DISMISS_MS = 6000;

const NotificationToast = ({ list = [], newNotification, onDismiss }) => {
  // Resolve the latest item to surface as toast. List ordering can vary —
  // pick whichever item is closest to "fresh": highest index field, else 0.
  const latest = useMemo(() => {
    if (!Array.isArray(list) || list.length === 0) return null;
    return list[0] || null;
  }, [list]);

  const [shown, setShown] = useState(null);
  const dismissTimerRef = useRef(null);

  useEffect(() => {
    if (newNotification && latest) {
      setShown(latest);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => {
        setShown(null);
        if (typeof onDismiss === "function") onDismiss();
      }, AUTO_DISMISS_MS);
    }
    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, [newNotification, latest, onDismiss]);

  if (!shown) return null;

  const theme = TYPE_THEME[detectType(shown)] || TYPE_THEME.info;
  const Icon = theme.Icon;

  const close = () => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    setShown(null);
    if (typeof onDismiss === "function") onDismiss();
  };

  const toast = (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 1500,
        width: 340,
        maxWidth: "calc(100vw - 32px)",
        background: "#ffffff",
        borderRadius: "10px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.18), 0 4px 8px rgba(15, 23, 42, 0.08)",
        overflow: "hidden",
        animation: "ppNotifToastIn 220ms cubic-bezier(0.16, 1, 0.3, 1)",
        "@keyframes ppNotifToastIn": {
          from: { transform: "translateY(-12px)", opacity: 0 },
          to:   { transform: "translateY(0)",     opacity: 1 },
        },
        display: "flex",
        alignItems: "stretch",
        fontFamily:
          'Inter,"Segoe UI",-apple-system,BlinkMacSystemFont,Roboto,sans-serif',
      }}
    >
      {/* Accent stripe — the only colourful element */}
      <Box sx={{ width: 4, background: theme.accent, flexShrink: 0 }} />

      {/* Icon tile */}
      <Box
        sx={{
          width: 40,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          paddingTop: 1.5,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            background: theme.tint,
            color: theme.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={16} />
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, padding: "10px 12px 10px 0", minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
          <Typography
            component="span"
            sx={{
              fontSize: "0.62rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: theme.accent,
            }}
          >
            {theme.label}
          </Typography>
          <IconButton
            size="small"
            onClick={close}
            aria-label="dismiss"
            sx={{
              width: 22,
              height: 22,
              color: "#94a3b8",
              "&:hover": { color: "#0f172a", background: "rgba(15,23,42,0.04)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 700,
            color: "#0f172a",
            lineHeight: 1.25,
            marginTop: "2px",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
          }}
        >
          {shown.title || "Notification"}
        </Typography>

        {shown.message && (
          <Typography
            sx={{
              fontSize: "0.74rem",
              color: "#475569",
              lineHeight: 1.35,
              marginTop: "2px",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
            }}
          >
            {shown.message}
          </Typography>
        )}
      </Box>
    </Box>
  );

  if (typeof document === "undefined") return toast;
  return ReactDOM.createPortal(toast, document.body);
};

export default NotificationToast;
